// Nieuw account aanmaken (Joris, 8 okt 2026: "ik wil nu een UMC-account aanmaken").
//
// Accounts staan in Airtable (Accounts) en groeperen bedrijven (Bedrijven, met een Gripp-id).
// Een account is dus geen bedrijf: de dubbelcheck vergelijkt alleen met accounts; een bedrijf met
// dezelfde naam is een kandidaat om te koppelen, of — hangt het al aan een account — een
// waarschuwing ("Enexis Personeel BV hoort al bij Enexis"). De engine maakt het record, werkt
// meteen de spiegel bij en bewaart website/e-maildomeinen; branche en portfolio gaan naar de
// gekoppelde bedrijven. Vangnet: na aanmaken 20 s "Ongedaan maken"
// (de engine verwijdert het record als er niets aan hangt, anders archiveert hij het).
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Autocomplete, Box, Button, Checkbox, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, FormControlLabel, MenuItem, Stack, TextField, Typography,
} from '@mui/material';
import toast from 'react-hot-toast';
import { BACKEND_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';
import { leesJson } from '../../utils/apiFout';
import { biedOngedaanAan } from '../../utils/ongedaan';

// Keuzelijst Accounts.Sector in Airtable; de engine weigert iets anders.
export const SECTOREN = ['Public', 'Semi-public', 'Finance', 'Agency', 'B2B', 'Only', 'Other'];
// Zelfde lijst als api/routes/bedrijf_branche.py (BRANCHES).
export const BRANCHES = [
  'Reclamebureau', 'Energie & netbeheer', 'Overheid & publiek', 'Zorg & welzijn',
  'Onderwijs & onderzoek', 'Financieel & verzekeraars', 'Industrie & techniek',
  'Bouw, vastgoed & infra', 'Retail & consument', 'Zakelijke dienstverlening',
  'Transport & logistiek', 'Cultuur, sport & non-profit', 'Overig',
];
// Zelfde sleutels als api/routes/klant_portfolios.py (PORTFOLIOS).
export const PORTFOLIOS = [
  { key: 'energie', label: 'Energie' },
  { key: 'bureaus', label: 'Bureaus (Very Video)' },
  { key: 'publiek', label: 'Publiek, zorg & dienstverlening' },
  { key: 'industrie', label: 'Industrie & retail' },
];

const LEEG = {
  naam: '', sector: '', accountmanagerIds: [], bedrijven: [], branche: '', portfolio: '',
  website: '', emailDomeinen: '',
};

const bedrijfLabel = (b) => `${b.naam}${b.grippId ? ` · Gripp ${b.grippId}` : ''}`;
const LEGE_CHECK = { bezig: false, dubbels: [], bedrijven: [], bedrijfOnderAccount: [] };

export default function NieuwAccountDialog({ open, onClose, onAangemaakt, onOngedaan }) {
  const [vorm, setVorm] = useState(LEEG);
  const [logo, setLogo] = useState(null);
  const [personen, setPersonen] = useState([]);
  const [check, setCheck] = useState(LEGE_CHECK);
  const [toch, setToch] = useState(false);
  const [zoek, setZoek] = useState('');
  const [zoekUit, setZoekUit] = useState([]);
  const [bezig, setBezig] = useState(false);
  const [fout, setFout] = useState('');
  const checkNr = useRef(0);
  const zoekNr = useRef(0);

  const zet = (veld) => (e) => setVorm((v) => ({ ...v, [veld]: e?.target ? e.target.value : e }));

  useEffect(() => {
    if (!open) return;
    setVorm(LEEG); setLogo(null); setToch(false); setFout(''); setZoek('');
    setCheck(LEGE_CHECK);
    authFetch(`${BACKEND_URL}/Api/klanten/personen/keuze`)
      .then((r) => leesJson(r, 'Personen laden'))
      .then((d) => setPersonen(d.personen || []))
      .catch((e) => toast.error(e.message));
  }, [open]);

  // Dubbelcheck terwijl je typt (400 ms rust); het laatste antwoord wint.
  useEffect(() => {
    const naam = vorm.naam.trim();
    setToch(false);
    // Elke wijziging maakt lopende antwoorden ongeldig, ook als de naam te kort wordt.
    const nr = ++checkNr.current;
    if (naam.length < 2) { setCheck(LEGE_CHECK); return undefined; }
    setCheck((c) => ({ ...c, bezig: true }));
    const t = setTimeout(() => {
      authFetch(`${BACKEND_URL}/Api/accounts-nieuw/dubbelcheck?naam=${encodeURIComponent(naam)}`)
        .then((r) => leesJson(r, 'Dubbelcheck'))
        .then((d) => {
          if (nr === checkNr.current) {
            setCheck({ bezig: false, dubbels: d.dubbels || [], bedrijven: d.bedrijven || [],
              bedrijfOnderAccount: d.bedrijfOnderAccount || [] });
          }
        })
        .catch((e) => { if (nr === checkNr.current) { setCheck(LEGE_CHECK); setFout(e.message); } });
    }, 400);
    return () => clearTimeout(t);
  }, [vorm.naam]);

  // Bedrijven zoeken (Gripp-bedrijven uit Bedrijven: geen contactpersonen of pure leveranciers).
  useEffect(() => {
    const term = zoek.trim();
    const nr = ++zoekNr.current;
    if (term.length < 2) { setZoekUit([]); return undefined; }
    const t = setTimeout(() => {
      authFetch(`${BACKEND_URL}/Api/accounts-nieuw/bedrijven?zoek=${encodeURIComponent(term)}`)
        .then((r) => leesJson(r, 'Bedrijven zoeken'))
        .then((d) => { if (nr === zoekNr.current) setZoekUit(d.bedrijven || []); })
        .catch(() => { if (nr === zoekNr.current) setZoekUit([]); });
    }, 300);
    return () => clearTimeout(t);
  }, [zoek]);

  const opId = useMemo(() => new Map(personen.map((p) => [p.id, p])), [personen]);
  const exact = check.dubbels.some((d) => d.exact);
  const onderAccount = check.bedrijfOnderAccount || [];
  const moetBevestigen = (check.dubbels.length > 0 || onderAccount.length > 0) && !exact;
  const gekozenIds = new Set(vorm.bedrijven.map((b) => b.id));
  const suggesties = check.bedrijven.filter((b) => !b.accountId && !gekozenIds.has(b.id));
  const kan = vorm.naam.trim().length >= 2 && !check.bezig && !exact && (!moetBevestigen || toch) && !bezig;

  const voegBedrijfToe = (b) => setVorm((v) => (v.bedrijven.some((x) => x.id === b.id) ? v : { ...v, bedrijven: [...v.bedrijven, b] }));

  // Logo als huisstijl-element. Het element-id komt altijd terug (ook als de upload faalt),
  // zodat ongedaan maken het kan opruimen.
  const uploadLogo = async (accountId) => {
    const r = await authFetch(`${BACKEND_URL}/Api/klanten/${accountId}/huisstijl`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ naam: 'Logo', type: 'Logo' }),
    });
    const d = await leesJson(r, 'Logo-element maken');
    const elementId = d.record?.id;
    if (!elementId) return null;
    try {
      const fd = new FormData();
      fd.append('file', logo);
      const up = await authFetch(`${BACKEND_URL}/Api/klanten/huisstijl/${elementId}/upload`, { method: 'POST', body: fd });
      if (!up.ok) toast.error('Logo uploaden mislukt — het account is wel aangemaakt');
    } catch {
      toast.error('Logo uploaden mislukt — het account is wel aangemaakt');
    }
    return elementId;
  };

  const maak = async () => {
    setBezig(true); setFout('');
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/accounts-nieuw`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          naam: vorm.naam.trim(), sector: vorm.sector || null, accountmanagerIds: vorm.accountmanagerIds,
          bedrijfIds: vorm.bedrijven.map((b) => b.id), branche: vorm.branche || null,
          portfolio: vorm.portfolio || null, website: vorm.website, emailDomeinen: vorm.emailDomeinen,
          tochAanmaken: toch,
        }),
      });
      if (r.status === 409) {
        const d = await r.json().catch(() => ({}));
        const det = d?.detail || {};
        setCheck((c) => ({ ...c, dubbels: det.dubbels || c.dubbels,
          bedrijfOnderAccount: det.bedrijfOnderAccount || c.bedrijfOnderAccount }));
        setFout(det.fout || 'Er bestaat al een vergelijkbaar account.');
        return;
      }
      const d = await leesJson(r, 'Account aanmaken');
      let logoId = null;
      if (logo) {
        try { logoId = await uploadLogo(d.id); } catch (e) { toast.error(e.message); }
      }
      if (d.alleenLokaal) {
        toast('Alleen in de testdatabase aangemaakt: op deze omgeving schrijft Toolframe niet naar Airtable.', { icon: 'ℹ️', duration: 8000 });
      }
      const naam = d.naam;
      biedOngedaanAan(`Account ${naam} aangemaakt`, async () => {
        // Eerst het account (de server telt ons eigen logo niet mee), pas daarna het logo weg.
        const pgLogo = String(logoId || '').replace(/^pg_/, '');
        const extra = /^\d+$/.test(pgLogo) ? `?logo=${pgLogo}` : '';
        const u = await authFetch(`${BACKEND_URL}/Api/accounts-nieuw/${d.id}/ongedaan${extra}`, { method: 'POST' });
        const uit = await leesJson(u, 'Ongedaan maken');
        if (logoId) {
          const weg = await authFetch(`${BACKEND_URL}/Api/klanten/huisstijl/${logoId}`, { method: 'DELETE' });
          if (!weg.ok) toast.error('Account teruggedraaid, maar het logo kon niet worden verwijderd');
        }
        if (uit.actie === 'archiveren') toast('Er hing al iets aan het account; het is gearchiveerd in plaats van verwijderd.', { icon: 'ℹ️' });
        onOngedaan?.(d.id, uit.actie);
      }, { duur: 20000, eigenKnop: true });
      onAangemaakt?.(d);
    } catch (e) {
      setFout(e.message);
    } finally {
      setBezig(false);
    }
  };

  return (
    <Dialog open={open} onClose={bezig ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 800 }}>Nieuw account</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField label="Naam" required autoFocus value={vorm.naam} onChange={zet('naam')}
            placeholder="Bijvoorbeeld UMC Utrecht" inputProps={{ maxLength: 200 }}
            InputProps={{ endAdornment: check.bezig ? <CircularProgress size={16} /> : null }} />

          {check.dubbels.length > 0 && (
            <Alert severity={exact ? 'error' : 'warning'} data-testid="dubbels">
              <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
                {exact ? 'Dit account bestaat al.' : 'Lijkt op een bestaand account:'}
              </Typography>
              {check.dubbels.map((d) => (
                <Typography key={d.id} variant="body2">
                  <a href={`/accounts/${d.id}/workspace`}>{d.naam}</a>
                  {` — ${d.reden}${d.status === 'Inactive' ? ' (inactief)' : ''}`}
                </Typography>
              ))}
            </Alert>
          )}

          {!exact && onderAccount.length > 0 && (
            <Alert severity="warning" data-testid="bedrijf-onder-account">
              <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
                Dit is al een bedrijf onder een bestaand account:
              </Typography>
              {onderAccount.map((b) => (
                <Typography key={b.id} variant="body2">
                  {`${b.naam} hoort bij account `}
                  <a href={`/accounts/${b.accountId}/workspace`}>{b.accountNaam || 'onbekend account'}</a>
                </Typography>
              ))}
            </Alert>
          )}

          {moetBevestigen && (
            <FormControlLabel sx={{ mt: -1 }}
              control={<Checkbox size="small" checked={toch} onChange={(e) => setToch(e.target.checked)} />}
              label="Het is een ander account, toch aanmaken" />
          )}

          <Autocomplete multiple options={personen} getOptionLabel={(p) => p.naam || ''}
            value={vorm.accountmanagerIds.map((id) => opId.get(id) || { id, naam: id })}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            onChange={(_e, w) => setVorm((v) => ({ ...v, accountmanagerIds: w.map((p) => p.id) }))}
            renderInput={(p) => <TextField {...p} label="Accountmanager" />} />

          <Autocomplete multiple filterOptions={(x) => x} options={zoekUit}
            getOptionLabel={bedrijfLabel} value={vorm.bedrijven}
            getOptionDisabled={(b) => !!b.accountId}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            inputValue={zoek} onInputChange={(_e, w, reden) => { if (reden !== 'reset') setZoek(w); }}
            onChange={(_e, w) => setVorm((v) => ({ ...v, bedrijven: w }))}
            noOptionsText={zoek.trim().length < 2 ? 'Typ minstens twee letters' : 'Geen bedrijf (Gripp) gevonden'}
            renderOption={(props, b) => (
              <li {...props} key={b.id}>
                {bedrijfLabel(b)}{b.accountId ? ' — hoort al bij een ander account' : ''}
              </li>
            )}
            renderInput={(p) => <TextField {...p} label="Bedrijven (Gripp) koppelen"
              helperText="Optioneel; een account groepeert een of meer bedrijven." />} />

          {suggesties.length > 0 && (
            <Box data-testid="suggesties">
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>Bedrijven (Gripp) met een vergelijkbare naam, nog zonder account</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 0.5 }}>
                {suggesties.map((b) => (
                  <Button key={b.id} size="small" variant="outlined" onClick={() => voegBedrijfToe(b)}
                    sx={{ textTransform: 'none' }}>+ {bedrijfLabel(b)}</Button>
                ))}
              </Box>
            </Box>
          )}

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
            <TextField select label="Sector" value={vorm.sector} onChange={zet('sector')}>
              <MenuItem value=""><em>Geen</em></MenuItem>
              {SECTOREN.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </TextField>
            <TextField select label="Branche" value={vorm.branche} onChange={zet('branche')}
              disabled={vorm.bedrijven.length === 0}
              helperText={vorm.bedrijven.length === 0 ? 'Na het koppelen van een bedrijf' : 'Voor de gekoppelde bedrijven'}>
              <MenuItem value=""><em>Niet wijzigen</em></MenuItem>
              {BRANCHES.map((b) => <MenuItem key={b} value={b}>{b}</MenuItem>)}
            </TextField>
            <TextField select label="Portfolio" value={vorm.portfolio} onChange={zet('portfolio')}
              disabled={vorm.bedrijven.length === 0} helperText="Leeg = afgeleid van de branche">
              <MenuItem value=""><em>Afgeleid</em></MenuItem>
              {PORTFOLIOS.map((p) => <MenuItem key={p.key} value={p.key}>{p.label}</MenuItem>)}
            </TextField>
            <TextField label="Website" value={vorm.website} onChange={zet('website')} placeholder="umcutrecht.nl" />
          </Box>

          <TextField label="E-maildomeinen" value={vorm.emailDomeinen} onChange={zet('emailDomeinen')}
            placeholder="umcutrecht.nl, umcu.nl" helperText="Gescheiden door komma's" />

          <Box>
            <Button component="label" size="small" variant="text" sx={{ textTransform: 'none', px: 0 }}>
              {logo ? `Logo: ${logo.name}` : 'Logo toevoegen (optioneel)'}
              <input hidden type="file" accept="image/*" data-testid="logo"
                onChange={(e) => setLogo(e.target.files?.[0] || null)} />
            </Button>
          </Box>

          {fout && <Alert severity="error">{fout}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={bezig}>Annuleren</Button>
        <Button variant="contained" onClick={maak} disabled={!kan}>
          {bezig ? 'Bezig…' : 'Account aanmaken'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
